import DeviationPriceUnitEnum from "@/constants/enums/DeviationPriceUnitEnum";
import { getKgPerUnit } from "@/utils/Contracts";
import { CurrencyCode } from "@/types";
import { InspectionContract } from "./types";

// Helpers for the inspection modal. Deviation costs aren't calculated here: the backend's preview prices them.

export { MAX_WEIGHT_KG } from "@/utils/weight";

/** Key of the original order's stream. Extra streams get generated keys. */
export const ORIGINAL_STREAM_KEY = "original";

/**
 * "#181 Contract name - product", the way contracts are listed in the contract list. The number tells apart
 * contracts with the same name, which a seller often has several of.
 */
export const getContractProductLabel = (
  contract: Pick<InspectionContract, "contractId" | "contractName" | "productName">
) =>
  `#${contract.contractId} ${contract.productName ? `${contract.contractName} - ${contract.productName}` : contract.contractName}`;

/** The unit code of "per order" deviation types, which are counted once. */
export const PER_ORDER_UNIT_CODE = DeviationPriceUnitEnum.PPO.code;

type PricedContract = Pick<InspectionContract, "price" | "priceUnit" | "product">;

/**
 * The contract price per kg, or null when it isn't known yet or can't be converted (stk on a product without
 * a weight per piece).
 */
export const getPricePerKg = (contract: PricedContract): number | null => {
  if (contract.price === null || !contract.priceUnit) return null;
  const kgPerUnit = getKgPerUnit(contract.priceUnit.unit, contract.product ?? undefined);
  return kgPerUnit ? contract.price / kgPerUnit : null;
};

/** The value of a weighed stream at its contract price, or null when the contract isn't priced per weight. */
export const getStreamValue = (weightKg: number, contract: PricedContract) => {
  const pricePerKg = getPricePerKg(contract);
  return pricePerKg === null ? null : weightKg * pricePerKg;
};

// Numbers as the modal shows them, in the user's language.

/** Separators for typed and displayed numbers: "1 000,5" in Norwegian and Danish, "1 000.5" in English. */
export const getNumberSeparators = (language: string) => ({
  thousandSeparator: " ",
  decimalSeparator: language.startsWith("en") ? "." : ",",
});

interface FormatNumberOptions {
  /** Most decimals shown. Default 2. */
  decimals?: number;
  /** Always show `decimals` decimals, as for prices ("0,40"). */
  fixed?: boolean;
  /** Prefix positive values with "+". */
  signed?: boolean;
}

/** Formats a number with the app's separators, e.g. 1110.5 → "1 110,5". */
export const formatNumber = (
  value: number,
  language: string,
  { decimals = 2, fixed = false, signed = false }: FormatNumberOptions = {}
) => {
  const { thousandSeparator, decimalSeparator } = getNumberSeparators(language);
  const plain = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: fixed ? decimals : 0,
    useGrouping: false,
  }).format(Math.abs(value));
  const [integer, fraction] = plain.split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
  const isZero = Number(plain) === 0;
  const sign = value < 0 && !isZero ? "-" : signed && value > 0 && !isZero ? "+" : "";
  return `${sign}${grouped}${fraction ? decimalSeparator + fraction : ""}`;
};

/** The currency as the design writes it: "kr" for the Scandinavian crowns, the code otherwise. */
export const getCurrencyLabel = (currency: CurrencyCode | string) =>
  ["NOK", "DKK", "SEK"].includes(currency) ? "kr" : currency;
